import React, { useEffect, useRef, useState } from 'react';
import { bool, func, node, string } from 'prop-types';
import Spinner from '../spinner';
import { Button } from '~/components/ui/button';

// Exported for callers that render an extra footerActions button matching the
// menu footer's Reset button styling (see wr-statuses-filter.js, reports-table).
export const ResetButton = (props) => <Button size="sm" variant="secondary" {...props} />;

const ButtonMenu = ({
	label,
	isDirty,
	onResetClick = undefined,
	cyName = 'default',
	children,
	onClick,
	isLoading = false,
	disabled = false,
	onOk,
	footerActions = undefined
}) => {
	const [showMenu, setShowMenu] = useState(false);
	const buttonMenuRef = useRef(null);

	const showMenuHandler = (e) => {
		e.preventDefault();
		if(onClick) {
			onClick();
		}

		setShowMenu(true);
	};

	const closeMenuHandler = (e) => {
		if(
			buttonMenuRef.current
			&& !buttonMenuRef.current.contains(e.target)
			&& !isTargetWhitelisted(e.target)
		) {
			setShowMenu(false);
		}
	};

	const handleResetFilter = () => {
		onResetClick();
		setShowMenu(false);
	};

	const handleOkClick = () => {
		if(typeof onOk === 'function') {
			onOk();
		}
		setShowMenu(false);
	};

	useEffect(() => {
		if(showMenu) {
			setTimeout(() => document.addEventListener('click', closeMenuHandler));
		}
		else {
			document.removeEventListener('click', closeMenuHandler);
		}

		return () => {
			document.removeEventListener('click', closeMenuHandler);
		};
	}, [showMenu]);

	const isTargetWhitelisted = (el) => {
		if(!el.parentNode) {
			return false;
		}

		const whitelist = ['btn-menu-react-select', 'px-react-select', 'px-contact-picker-tag'];

		const isWhitelisted = whitelist.some((cl) => {
			return [...el.classList].filter((c) => c.includes(cl)).length > 0;
		});

		if(isWhitelisted) {
			return true;
		}

		return el.parentNode && isTargetWhitelisted(el.parentNode);
	};

	const isActive = showMenu || isDirty;

	return (
		<div ref={buttonMenuRef} className="relative flex items-center">
			<Button
				variant={isActive ? 'default' : 'secondary'}
				className="mr-4 capitalize"
				onClick={showMenuHandler}
				data-cy={`${cyName}-button-menu-btn`}
				data-testid={`${cyName}-button-menu-btn`}
				disabled={disabled}
			>
				{label}
			</Button>

			{showMenu && (
				<nav
					className="absolute top-full left-0 mt-2 rounded-xs bg-panel shadow-card-hover p-6 z-dropdown"
					style={{ minWidth: '18rem' }}
				>
					{isLoading
						? (
							<Spinner small />
						)
						: (
							<>
								<div>{children}</div>
								<footer className="flex flex-row-reverse justify-between pt-8">
									<Button
										size="sm"
										onClick={() => handleOkClick()}
										data-cy={`${cyName}-button-menu-ok`}
										data-testid={`${cyName}-button-menu-ok`}
									>
										OK
									</Button>
									{(onResetClick || footerActions) && (
										<div className="flex items-center gap-3">
											{onResetClick && (
												<ResetButton
													onClick={handleResetFilter}
													data-cy={`${cyName}-button-menu-reset`}
													data-testid={`${cyName}-button-menu-reset`}
												>
													Reset
												</ResetButton>
											)}
											{footerActions}
										</div>
									)}
								</footer>
							</>
						)}
				</nav>
			)}
		</div>
	);
};

ButtonMenu.propTypes = {
	label: string.isRequired,
	isDirty: bool.isRequired,
	onOk: func,
	onResetClick: func,
	cyName: string,
	children: node.isRequired,
	onClick: func,
	isLoading: bool,
	disabled: bool,
	footerActions: node
};

export default ButtonMenu;
